import { apiGet } from './apiClient.js';

/**
 * Audit Service
 * For querying audit events
 */

/**
 * Format timestamp for display
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string}
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) return '—';
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return date.toLocaleString();
}

/**
 * Get default time window (last 24 hours)
 * @returns {object} { since: ISO string, until: ISO string }
 */
export function getDefaultTimeWindow() {
  const until = new Date();
  const since = new Date(until.getTime() - 24 * 60 * 60 * 1000);
  return {
    since: since.toISOString(),
    until: until.toISOString(),
  };
}

/**
 * Get audit events with filters
 * @param {object} params
 * @param {number} params.limit - Max results (default 50, max 200)
 * @param {string} params.cursor - Cursor for pagination
 * @param {string} params.since - ISO timestamp (start)
 * @param {string} params.until - ISO timestamp (end)
 * @param {string} params.action - Filter by action (exact match)
 * @param {string} params.actionPrefix - Filter by action prefix (e.g., "users." or "rbac.")
 * @param {string|string[]} params.actions - Filter by multiple actions (comma-separated or array)
 * @param {string} params.actor_id - Filter by actor ID
 * @param {string} params.request_id - Filter by request ID
 * @param {string} params.target_id - Filter by target ID
 * @param {string} params.outcome - Filter by outcome (success/failure)
 * @param {string} params.integration - Filter by integration code
 * @param {string} params.q - Search term
 * @returns {Promise<object>}
 */
export async function getAuditEvents(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.since) queryParams.append('since', params.since);
  if (params.until) queryParams.append('until', params.until);
  if (params.action) queryParams.append('action', params.action);
  if (params.actionPrefix) queryParams.append('actionPrefix', params.actionPrefix);
  if (params.actions) {
    if (Array.isArray(params.actions)) {
      params.actions.forEach(action => queryParams.append('actions', action));
    } else {
      queryParams.append('actions', params.actions);
    }
  }
  if (params.actor_id) queryParams.append('actor_id', params.actor_id);
  if (params.request_id) queryParams.append('request_id', params.request_id);
  if (params.target_id) queryParams.append('target_id', params.target_id);
  if (params.outcome) queryParams.append('outcome', params.outcome);
  if (params.integration) queryParams.append('integration', params.integration);
  if (params.q) queryParams.append('q', params.q);

  const queryString = queryParams.toString();
  return apiGet(`/admin/audit-events${queryString ? `?${queryString}` : ''}`);
}
